import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type ChildTicketStatusFormProps = {
  statusId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const initialValues = {
  code: '',
  label: '',
  default_sla_minutes: '' as string | number,
  auto_pause: false,
  triggers_parent_status: '',
}

export function ChildTicketStatusForm({
  statusId,
  onSuccess,
  onCancel,
}: ChildTicketStatusFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!statusId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)

  const loadOne = useCallback(async () => {
    if (!statusId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/service-catalog/child-ticket-statuses/${statusId}`)
      const r = data?.data ?? data
      setForm({
        code: r.code ?? '',
        label: r.label ?? '',
        default_sla_minutes: r.default_sla_minutes ?? '',
        auto_pause: r.auto_pause === true,
        triggers_parent_status: r.triggers_parent_status ?? '',
      })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [statusId, showError])

  useEffect(() => {
    if (statusId) {
      loadOne()
    } else {
      setForm(initialValues)
    }
  }, [statusId, loadOne])

  const updateField = (field: keyof typeof form, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        code: form.code.trim(),
        label: form.label.trim() || null,
        default_sla_minutes: form.default_sla_minutes === '' ? null : Number(form.default_sla_minutes),
        auto_pause: form.auto_pause,
        triggers_parent_status: form.triggers_parent_status.trim() || null,
      }
      if (statusId) {
        await apiClient.put(`/service-catalog/child-ticket-statuses/${statusId}`, payload)
        showSuccess('Success', 'Child ticket status updated successfully')
      } else {
        await apiClient.post('/service-catalog/child-ticket-statuses', payload)
        showSuccess('Success', 'Child ticket status created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box py={8}>
        <Box as="span">Loading...</Box>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Code <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.code?.length} isRequired>
            <Input
              value={form.code}
              onChange={(e) => updateField('code', e.target.value)}
              placeholder="e.g. IN_PROGRESS"
              isDisabled={!!statusId}
            />
            {!!statusId && (
              <Box as="span" fontSize="sm" color="gray.500">Code cannot be changed after creation.</Box>
            )}
            <FormErrorMessage>{errors.code?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Label</FormLabel>
          <FormControl isInvalid={!!errors.label?.length}>
            <Input
              value={form.label}
              onChange={(e) => updateField('label', e.target.value)}
              placeholder="e.g. In Progress"
            />
            <FormErrorMessage>{errors.label?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Default SLA (minutes)</FormLabel>
          <FormControl isInvalid={!!errors.default_sla_minutes?.length}>
            <Input
              type="number"
              min={0}
              value={form.default_sla_minutes === '' ? '' : form.default_sla_minutes}
              onChange={(e) => updateField('default_sla_minutes', e.target.value === '' ? '' : e.target.value)}
              placeholder="Optional"
            />
            <FormErrorMessage>{errors.default_sla_minutes?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Triggers parent status (code)</FormLabel>
          <FormControl isInvalid={!!errors.triggers_parent_status?.length}>
            <Input
              value={form.triggers_parent_status}
              onChange={(e) => updateField('triggers_parent_status', e.target.value)}
              placeholder="Parent status code when this child status is set"
            />
            <FormErrorMessage>{errors.triggers_parent_status?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Auto pause</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.auto_pause}
              onChange={(e) => updateField('auto_pause', e.target.checked)}
              colorScheme="blue"
            >
              <Box as="span">Automatically pause SLA when this status is set.</Box>
            </Checkbox>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={saving}
            loadingText={statusId ? 'Saving...' : 'Creating...'}
          >
            {statusId ? 'Save' : 'Create Child Ticket Status'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
